import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COMPANY = {
  name: 'SwissChem',
  address: '82-C Mehar Sadiq Market, Railway Road, Faisalabad',
  phone: '+92 300 7671404',
  landline: '041-2611464',
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    .replace(/ /g, '-');
};

const fmtNum = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

/**
 * Generate and download a K&M-style Accounts Ledger PDF for one party.
 * @param {object} ledger - result of GET /api/chemical/ledger/:party_type/:party_id
 * @param {string} party_type - 'vendor' | 'customer'
 * @param {object} opts - { from_date, to_date }
 */
export const generateLedgerPdf = (ledger, party_type, opts = {}) => {
  const { party, opening_balance, opening_suffix, transactions, total_debit,
          total_credit, closing_balance, closing_suffix, month_breakup } = ledger;
  const { from_date, to_date } = opts;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 10;
  let y = 12;

  // ─── Header ─────────────────────────────────
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.4);
  doc.rect(marginX, y - 4, 12, 12);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('SC', marginX + 6, y + 3.5, { align: 'center' });

  doc.setFontSize(9);
  doc.text(COMPANY.name, marginX + 15, y + 1);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(20, 20, 20);
  doc.text('Accounts Ledger', pageWidth / 2, y + 2, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('(Un-Audited)', pageWidth / 2, y + 6, { align: 'center' });

  doc.setFontSize(7);
  doc.setTextColor(90, 90, 90);
  const rightX = pageWidth - marginX;
  doc.text(`Printed on: ${new Date().toLocaleString('en-GB')}`, rightX, y - 2, { align: 'right' });

  y += 12;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(40, 40, 40);
  doc.text(`From : ${formatDate(from_date) !== '—' ? formatDate(from_date) : '—'}    To : ${formatDate(to_date) !== '—' ? formatDate(to_date) : new Date().toLocaleDateString('en-GB')}`, marginX, y);
  y += 6;

  doc.setFont('helvetica', 'bold');
  doc.text(`A/C Name : ${party.name}`, marginX, y);
  doc.text(`Opening -->  ${fmtNum(opening_balance)} ${opening_suffix}`, rightX, y, { align: 'right' });
  y += 6;

  // ─── Table ──────────────────────────────────
  const rows = transactions.map(t => [
    formatDate(t.date),
    t.voc_no || '—',
    t.narration,
    t.bill_no || '—',
    t.cheq_no || '—',
    t.debit ? fmtNum(t.debit) : '',
    t.credit ? fmtNum(t.credit) : '',
    `${fmtNum(t.balance)} ${t.balance >= 0 ? (party_type === 'vendor' ? 'CR' : 'DR') : (party_type === 'vendor' ? 'DR' : 'CR')}`,
  ]);

  autoTable(doc, {
    startY: y,
    head: [['V.Date', 'Voc.#', 'Narration', 'Bill #', 'Cheq #', 'Debit', 'Credit', 'Balance']],
    body: rows.length ? rows : [['—', '—', 'No transactions', '—', '—', '—', '—', '—']],
    foot: [['', '', '', '', 'Total > > >', fmtNum(total_debit), fmtNum(total_credit), '']],
    theme: 'grid',
    headStyles: { fillColor: [235, 235, 235], textColor: [30, 30, 30], fontStyle: 'bold', fontSize: 7.5, lineColor: [160, 160, 160], lineWidth: 0.2 },
    bodyStyles: { fontSize: 7, textColor: [30, 30, 30], lineColor: [180, 180, 180], lineWidth: 0.15, valign: 'top' },
    footStyles: { fillColor: [245, 245, 245], textColor: [30, 30, 30], fontStyle: 'bold', fontSize: 7.5, lineColor: [160, 160, 160], lineWidth: 0.2 },
    columnStyles: {
      0: { cellWidth: 16 },
      1: { cellWidth: 18 },
      2: { cellWidth: 'auto' },
      3: { cellWidth: 16, halign: 'center' },
      4: { cellWidth: 16, halign: 'center' },
      5: { cellWidth: 20, halign: 'right' },
      6: { cellWidth: 20, halign: 'right' },
      7: { cellWidth: 24, halign: 'right' },
    },
    margin: { left: marginX, right: marginX },
  });

  let afterY = doc.lastAutoTable.finalY + 6;

  // ─── Ledger balance line ───
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(`Ledger Balance > > >   ${fmtNum(closing_balance)} ${closing_suffix}`, rightX, afterY, { align: 'right' });
  afterY += 8;

  // ─── Month breakup box ───
  if (month_breakup && month_breakup.length > 0) {
    const boxW = 55;
    const boxX = rightX - boxW;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.3);
    doc.text('Breakup', boxX, afterY);
    afterY += 2;
    doc.rect(boxX, afterY, boxW, month_breakup.length * 5 + 2);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    let by = afterY + 4;
    month_breakup.forEach(m => {
      doc.text(m.month, boxX + 2, by);
      doc.text(fmtNum(m.net), boxX + boxW - 2, by, { align: 'right' });
      by += 5;
    });
  }

  // ─── Page number ───
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text('Page 1 of 1', rightX, pageHeight - 6, { align: 'right' });

  doc.save(`Ledger_${party.name.replace(/\s+/g, '_')}.pdf`);
};